import { Alert, ChipMetadata, LinkButton, Tooltip } from '@pmi/dsm-react';
import {
  BookmarkSolidIcon,
  CertificationSolidIcon,
  FriendsSolidIcon,
  LocationOutlineIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { TriangleAlertIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { ArrowForwardIcon } from '@pmi/dsm-react/dist/components/icons/24';
import { AlertWarningIcon } from '@pmi/dsm-react/dist/components/icons/fa/16';
import { useEffect, useState } from 'react';
import { isPayByUser, mapPaymentArrangementText } from '../../constants';
import { getProductsCountLabel, toDayMonthYear } from '../../helpers';
import { AgreementApiModel } from '../../models/DealModel';
import { CategoryType } from '../CategoryLabel/models';
import RenderHtml from '../RenderHtml';
import styles from './agreementCard.module.scss';
import {
  dealHasExpired,
  dealHasNoAvailableProducts,
} from '../../utils/dataHelpers';

/**
 * Props for the AgreementCard component.
 */
interface AgreementCardProps {
  /** Title to display for the agreement card */
  title: string;
  /** Label for the main action button */
  btnLabel: string;
  /** Optional short label for the button (for smaller screens) */
  btnShortLabel?: string;
  /** Optional click handler for the action button */
  onClick?: () => void;
  /** Whether the agreement is expired */
  expired?: boolean;
  /** Additional class name(s) for the card */
  className?: string;
  /** Whether the agreement is disabled */
  isDisabledAgreement?: boolean;
  /** Whether the agreement is expiring soon */
  isExpiring?: boolean;
  /** Whether to show status pills (Expired, Disabled, etc.) */
  showStatusPills?: boolean;
  /** Whether the agreement is unassignable */
  unassignable?: boolean;
  /** Deal data for the agreement */
  deal: AgreementApiModel;
  btnDisabled?: boolean;
}

/**
 * Renders an agreement card displaying deal information, product types, and action buttons.
 * Shows status pills, zone compatibility, payment arrangement, and product lists by type.
 * Handles expired/disabled/expiring states and disables actions accordingly.
 *
 * @param {AgreementCardProps} props - The props for the AgreementCard component.
 * @returns {JSX.Element} The rendered agreement card.
 */
export default function AgreementCard({
  title,
  btnLabel,
  btnShortLabel = undefined,
  onClick = undefined,
  expired = false,
  className = '',
  isDisabledAgreement = false,
  isExpiring = false,
  showStatusPills = false,
  unassignable = false,
  deal,
  btnDisabled = false,
}: AgreementCardProps) {
  const isExpired = dealHasExpired(deal);
  const isSoldOut = !isExpired && dealHasNoAvailableProducts(deal);
  // Button is disabled if agreement is expired or disabled
  const disabled = isExpired || isSoldOut || !deal.enabled;
  const payByUser =
    deal.isPayByUser || isPayByUser(deal.paymentArrangement || '');

  /**
   * Renders status pills (Expired, Disabled, Expires Soon) if enabled.
   * @returns {JSX.Element | null}
   */
  // @ts-ignore
  const chipStatus = () => {
    if (!showStatusPills) {
      return false;
    }
    const hasPill = expired || isDisabledAgreement || isExpiring;

    const pills: { text: string; variant: any }[] = [];

    if (expired) {
      pills.push({
        text: 'Expired',
        variant: 'red',
      });
    }
    if (isDisabledAgreement) {
      pills.push({
        text: 'Disabled',
        variant: 'red',
      });
    }
    if (isExpiring) {
      pills.push({
        text: 'Expires Soon',
        variant: 'tangerine',
      });
    }

    if (hasPill) {
      return (
        <div
          className="d-flex align-items-center align-content-center"
          key={`pill-${new Date().getTime() + 1}`}
        >
          {pills.map((p) => (
            <ChipMetadata
              text={p.text}
              variant={p.variant}
              className="small mx-1"
              icon={<TriangleAlertIcon className={styles.smallIcon} />}
            />
          ))}
        </div>
      );
    }
    return null;
  };

  /**
   * Renders the title bar with deal name, id, status pills, zone compatibility, and payment arrangement.
   * @param {string} title - The title to display.
   * @returns {JSX.Element}
   */
  const titleBar = (value: string) => {
    const status = chipStatus();
    return (
      <div className="d-flex fw-normal flex-column flex-sm-row align-items-start align-items-sm-center mb-1">
        <strong className={styles.titleBlock}>
          {value.trim().length > 0 ? deal.dealName : deal.id}
        </strong>
        {value.trim().length > 0 && (
          <span className="small d-flex mx-1 flex-grow-1">{deal.id}</span>
        )}
        <div className="small d-flex flex-column flex-sm-row">
          {status && (
            <span className="d-flex align-items-center  mb-1 mb-sm-0">
              <span className="text-muted mx-2 d-none d-sm-inline">|</span>
              <span>{status}</span>
            </span>
          )}
          <span className="d-flex align-items-center mb-1 mb-sm-0">
            <span className="text-muted ms-2  d-none d-sm-inline">|</span>
            <LocationOutlineIcon
              fontSize="sm"
              className="mr-2  d-none d-sm-inline"
            />{' '}
            Zone Compatibility:
            <strong className="ms-1">
              {Array.isArray(deal.zones) &&
                deal.zones.length > 0 &&
                deal.zones
                  .map((z) =>
                    Array.isArray(z)
                      ? `${z[0]?.toUpperCase()} ${z?.slice(1)}`
                      : `Zone ${z?.toUpperCase()}`,
                  )
                  .join(' + ')}
              {deal.zones.length === 0 && 'All Zones'}
            </strong>
          </span>
          <span className="d-flex align-items-center mb-1 mb-sm-0">
            <span className="text-muted mx-2 d-none d-sm-inline">|</span>Payment
            Arrangement:
            <strong className="ms-1">{mapPaymentArrangementText(deal)}</strong>
          </span>
        </div>
      </div>
    );
  };

  /**
   * Returns a string of HTML representing the product list for the given types.
   * @param {CategoryType[]} types - The product types to include.
   * @returns {string | null}
   */
  const productList = (types: CategoryType[]) => {
    const prods = deal.products.map((p) => {
      if (!types.includes(p.type)) {
        return null;
      }

      const productName =
        p.name && p.name.length > 0 ? p.name.trim() : p.sku.trim();
      let quantity: number | string;
      if (!disabled) {
        if (expired) {
          quantity = p.quantityRedeemed;
        } else if (payByUser) {
          quantity = '∞';
        } else {
          quantity = p.quantityRemaining;
        }
      } else {
        quantity = '0';
      }
      const quantityLabel = expired ? 'Redeemed' : 'Available';
      return `<span class="d-block d-sm-inline">${productName} - ${quantity} ${quantityLabel}</span>`;
    });
    return prods.length > 0
      ? prods
          .filter((p) => p)
          .join('<span class="mx-2 d-none d-sm-inline">|</span>')
      : null;
  };

  /**
   * Tracks which product types are present in the deal.
   */
  const [hasProductType, setHasProductType] = useState<{
    digital: boolean;
    eLearning: boolean;
    certificate: boolean;
    membership: boolean;
  }>({
    digital: false,
    eLearning: false,
    certificate: false,
    membership: false,
  });

  const [isAllNoProductType, setIsAllNoProductType] = useState(false);

  // Update product type presence when deal changes
  useEffect(() => {
    setHasProductType({
      digital:
        deal.products.find((p) => p.type === 'Digital Product') !== undefined,
      eLearning:
        deal.products.find((p) => p.type === 'eLearning') !== undefined,
      certificate:
        deal.products.find((p) => p.type === 'Certification') !== undefined,
      membership:
        deal.products.find((p) =>
          ['Membership', 'Chapter Membership'].includes(p.type),
        ) !== undefined,
    });
  }, [deal]);

  useEffect(() => {
    setIsAllNoProductType(
      !(
        hasProductType.digital ||
        hasProductType.eLearning ||
        hasProductType.certificate ||
        hasProductType.membership
      ),
    );
  }, [hasProductType]);

  return (
    <li
      key={`agreementCard${deal.id.replace('-', '')}`}
      className={`${styles.prodGroupCard} d-flex flex-row justify-content-between border mb-3 text-start`}
    >
      <div
        className={`${styles.productInfo} d-flex list-item w-100 ${className}`}
      >
        <div className="w-100 overflow-auto">
          <div className="d-flex justify-content-between">
            <div className="d-flex flex-column">
              <div className="title mb-2 d-block">
                <div className="d-block">
                  <span>{titleBar(title)}</span>
                </div>
                <div className="d-block small text-muted">
                  {getProductsCountLabel(deal.products.length)}
                  <span className="mx-2">|</span>
                  {expired ? 'Expired' : 'Expires'} on:{' '}
                  {toDayMonthYear(deal.expiration)}
                </div>
              </div>
            </div>
            {onClick && (
              <div className="ms-4 d-none d-sm-block">
                <Tooltip
                  content={
                    unassignable
                      ? 'Agreement unavailable. Please contact PartnerSupport@pmi.org.'
                      : 'You cannot select any expired Agreement.'
                  }
                  type="sm"
                  blockTriggers={!disabled && !btnDisabled}
                  placement="top"
                  className="darkTooltip"
                >
                  <LinkButton
                    size="sm"
                    variant="primary"
                    titleText={btnLabel}
                    className={`border d-none d-md-flex fw-normal ${
                      disabled || btnDisabled ? styles.btnDisabled : ''
                    }`}
                    onClick={() => {
                      if (!disabled && !btnDisabled) {
                        onClick();
                      }
                    }}
                    disabled={disabled || btnDisabled}
                  />
                  <LinkButton
                    size="sm"
                    variant="primary"
                    titleText={btnShortLabel || btnLabel}
                    icon={ArrowForwardIcon}
                    className={`border d-none d-sm-flex d-md-none fw-normal ${
                      disabled ? styles.btnDisabled : ''
                    }`}
                    onClick={() => {
                      if (!disabled) {
                        onClick();
                      }
                    }}
                    disabled={disabled}
                  />
                </Tooltip>
              </div>
            )}
          </div>
          {onClick && (
            <div className="d-block d-sm-none">
              <Tooltip
                content={
                  unassignable
                    ? 'Agreement unavailable. Please contact PartnerSupport@pmi.org.'
                    : 'You cannot select any expired Agreement.'
                }
                type="sm"
                blockTriggers={!expired && !unassignable}
                placement="top"
                className="darkTooltip"
              >
                <LinkButton
                  iconAlign="right"
                  variant="primary"
                  titleText={btnLabel}
                  icon={ArrowForwardIcon}
                  className={`border ${disabled ? styles.btnDisabled : ''}`}
                  onClick={() => {
                    if (!disabled) {
                      onClick();
                    }
                  }}
                  disabled={
                    disabled || deal.products.length === 0 || btnDisabled
                  }
                />
              </Tooltip>
            </div>
          )}
          <hr className="my-3" />
          <div className={styles.productAvailability}>
            <div className="d-flex flex-column small">
              {hasProductType.eLearning && (
                <div className="d-block d-sm-flex my-1">
                  <div className={`${styles.productInfoTitle} text-info`}>
                    <BookmarkSolidIcon fontSize="sm" /> Online Courses
                  </div>
                  <div className={styles.productInfoSep}>|</div>
                  <RenderHtml
                    html={productList(['eLearning'])}
                    tag="div"
                    className="d-flex flex-wrap text-wrap flex-grow-1 text-muted mx-2"
                  />
                </div>
              )}
              {hasProductType.membership && (
                <div className="d-block d-sm-flex my-1">
                  <div className={`${styles.productInfoTitle} text-primary`}>
                    <FriendsSolidIcon fontSize="sm" /> Membership
                  </div>
                  <div className={styles.productInfoSep}>|</div>
                  <RenderHtml
                    html={productList(['Membership', 'Chapter Membership'])}
                    tag="div"
                    className="d-flex flex-wrap flex-grow-1 text-muted mx-2"
                  />
                </div>
              )}
              {hasProductType.certificate && (
                <div className="d-block d-sm-flex my-1">
                  <div className={`${styles.productInfoTitle} text-danger`}>
                    <CertificationSolidIcon fontSize="sm" /> Certification
                  </div>
                  <div className={styles.productInfoSep}>|</div>
                  <RenderHtml
                    html={productList(['Certification'])}
                    tag="div"
                    className="d-flex flex-wrap flex-grow-1 text-muted mx-2"
                  />
                </div>
              )}
              {hasProductType.digital && (
                <div className="d-block d-sm-flex my-1">
                  <div className={`${styles.productInfoTitle} text-success`}>
                    <BookmarkSolidIcon fontSize="sm" /> Digital Products
                  </div>
                  <div className={styles.productInfoSep}>|</div>
                  <RenderHtml
                    html={productList(['Digital Product'])}
                    tag="div"
                    className="d-flex flex-wrap flex-grow-1 text-muted mx-2"
                  />
                </div>
              )}
              {isAllNoProductType && (
                <span className="d-block my-1">
                  <Alert
                    alertText="Unable to retrieve product details."
                    variant="warning"
                    icon={AlertWarningIcon}
                  />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}
